import { createApi } from '@reduxjs/toolkit/query/react';
import { routes } from 'Api/apiRoutes';
import { baseQuery, getLocalizationHeader, buildUrl } from '../Utils/queryUtils';
import { IJsonResponse } from 'Types/Interface/IApiRequestResponseBody';
import { IBrandHistory } from 'Types/Interface/IBrand';
import { IProduct, IProductDetails } from 'Types/Interface/IProduct';
import { IPaginatedResponse } from './types';
import { TagType } from 'Types/Enums';

export const productApi = createApi({
	reducerPath: 'productApi',
	baseQuery,
	tagTypes: [TagType.Product],
	endpoints: (builder) => ({
		getProductDetails: builder.query<IProductDetails, { productId: string; locale: string }>({
			query: ({ productId, locale }) => ({
				url: buildUrl(routes.productDetails, { productId }),
				headers: getLocalizationHeader(locale),
			}),
			providesTags: [TagType.Product],
		}),
		getAllProducts: builder.query<
			IPaginatedResponse<IProduct[]>,
			{ pageNumber?: number; pageSize?: number; categoryId?: string; locale: string }
		>({
			query: ({ pageNumber, pageSize, categoryId, locale }) => ({
				url: `${routes.products}?categoryId=${categoryId}&pageSize=${pageSize}&pageNumber=${pageNumber}`,
				headers: getLocalizationHeader(locale),
			}),
		}),
		getProductByCategory: builder.query<IJsonResponse<IProduct[]>, { categoryId: string; locale: string }>({
			query: ({ categoryId, locale }) => ({
				url: buildUrl(routes.categoryProducts, { categoryId }),
				headers: getLocalizationHeader(locale),
			}),
		}),
		getAvailableSurveysQrCodes: builder.query<IJsonResponse<string[]>, { productId?: string; brandId?: string; locale: string }>(
			{
				query: ({ productId, brandId, locale }) => {
					const params = new URLSearchParams();

					if (productId) {
						params.append('productId', productId);
					}

					if (brandId) {
						params.append('brandId', brandId);
					}

					return {
						url: `${routes.productAvailableSurveysQrCodes}?${params.toString()}`,
						headers: getLocalizationHeader(locale),
					};
				},
				providesTags: [TagType.Product],
			}
		),
		favoriteProduct: builder.mutation<unknown, { productId: string | undefined; locale: string }>({
			query: ({ productId, locale }) => ({
				url: buildUrl(routes.productFavorite, { productId }),
				method: 'POST',
				headers: getLocalizationHeader(locale),
			}),
			invalidatesTags: [TagType.Product],
		}),
		unFavoriteProduct: builder.mutation<unknown, { productId: string; locale: string }>({
			query: ({ productId, locale }) => ({
				url: buildUrl(routes.productUnfavorite, { productId }),
				method: 'POST',
				headers: getLocalizationHeader(locale),
			}),
			invalidatesTags: [TagType.Product],
		}),
		getProductScanHistory: builder.query<IJsonResponse<IBrandHistory>, { productId: string; locale: string }>({
			query: ({ productId, locale }) => ({
				url: buildUrl(routes.productScanHistory, { productId }),
				headers: getLocalizationHeader(locale),
			}),
		}),
	}),
});

export const {
	useGetProductDetailsQuery,
	useGetAllProductsQuery,
	useGetProductByCategoryQuery,
	useFavoriteProductMutation,
	useUnFavoriteProductMutation,
	useGetProductScanHistoryQuery,
	useGetAvailableSurveysQrCodesQuery,
} = productApi;